import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const PHASES = [
  {
    phase: "VISION • PHASE I",
    title: "Monthly Founders Meeting",
    body: "A monthly working session for the founding group. The initial circle is capped at 10 founders — five seats are already taken, so five remain.",
    badge: "Q4 2026",
    img: "https://images.unsplash.com/photo-1515169067868-5387ec356754?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGRpbm5lciUyMG1lZXRpbmd8ZW58MHx8fHwxNzkyMDEyODE3fDA&ixlib=rb-4.1.0&q=85",
    alt: "Founders meeting around a private table",
    testid: "roadmap-card-chapters",
  },
  {
    phase: "VISION • PHASE II",
    title: "Educational Incubator",
    body: "Practical AI masterclasses taught primarily by AIX founders, with selected outside educators brought in for specialized topics.",
    badge: "Q1 2027",
    img: "https://images.unsplash.com/photo-1599837565318-67429bde7162?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxzb2Z0d2FyZSUyMHNjcmVlbiUyMGRhcmt8ZW58MHx8fDE3OTIwMTI4MjJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "AI curriculum and peer learning session",
    testid: "roadmap-card-education",
  },
  {
    phase: "VISION • PHASE III",
    title: "Open Membership Enrollment",
    body: "We begin accepting additional members beyond the founding circle. Membership will likely be fee-based.",
    badge: "Q1 2027",
    img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "New members joining a working session",
    testid: "roadmap-card-enrollment",
  },
  {
    phase: "VISION • PHASE IV",
    title: "Annual Flagship Summit",
    body: "The goal: an on-site, in-person event for an elite group of AI developers and professionals — the first physical gathering of the network.",
    badge: "Q3 2027",
    img: "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHx0ZWNoJTIwY29uZmVyZW5jZSUyMHByZXNlbnRhdGlvbnxlbnwwfHx8fDE3OTIwMTI4MTB8MA&ixlib=rb-4.1.0&q=85",
    alt: "On-site summit for AI developers and professionals",
    testid: "roadmap-card-summit",
  },
];

export function Roadmap() {
  return (
    <section id="vision" className="py-24 md:py-32 max-w-6xl mx-auto px-6 scroll-mt-20" data-testid="vision-section">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <span className="font-mono-lbl text-xs text-aix-cyandeep tracking-widest uppercase">03 / ROADMAP</span>
        <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mt-2">
          What Is Coming Next
        </h2>
        <p className="text-slate-500 text-sm mt-3">
          Our stated long-term vision. Not live today, but actively being shaped with foundational members.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PHASES.map(({ phase, title, body, badge, img, alt, testid }) => (
          <div
            key={title}
            className="border border-slate-200 bg-white shadow-sm rounded-xl relative overflow-hidden group hover:border-aix-cyan/60 transition-colors flex flex-col"
            data-testid={testid}
          >
            <div className="h-36 relative overflow-hidden">
              <img
                src={img}
                alt={alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent"></div>
            </div>
            <div className="p-6 flex flex-col flex-grow">
              <div className="font-mono-lbl text-xs text-aix-cyandeep font-bold mb-4">{phase}</div>
              <h3 className="font-display text-xl font-bold text-slate-900 mb-2">{title}</h3>
              <p className="text-slate-500 text-xs leading-relaxed mb-6">{body}</p>
              <span className="font-mono-lbl text-[10px] text-slate-500 uppercase bg-slate-100 px-2 py-1 rounded border border-slate-200 mt-auto self-start">
                {badge}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function FoundersTeaser() {
  return (
    <section className="pb-24 max-w-6xl mx-auto px-6" data-testid="founders-teaser">
      <Link
        to="/founders"
        className="flex items-center gap-5 border border-slate-200 bg-white shadow-sm rounded-2xl p-6 hover:border-aix-cyan/60 hover:shadow-md transition-all group"
        data-testid="founders-teaser-link"
      >
        <img
          src="/assets/joe-beccalori.jpg"
          alt="Joe Beccalori, Founder of AI Advantage Exchange"
          className="w-16 h-16 rounded-full object-cover border border-slate-200 shrink-0"
          data-testid="founders-teaser-photo"
        />
        <div className="min-w-0">
          <span className="font-mono-lbl text-[10px] text-aix-cyandeep tracking-widest uppercase">
            The People Behind AIX
          </span>
          <p className="text-sm text-slate-600 mt-1">
            Founded by <span className="font-semibold text-slate-900">Joe Beccalori</span> — Founder &amp; CEO, Interact Marketing · 30+ years in marketing &amp; emerging tech
          </p>
        </div>
        <span className="ml-auto hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-aix-navy group-hover:text-[#16407F] whitespace-nowrap">
          Meet the founding members <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </Link>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50 py-12" data-testid="footer">
      <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-3">
          <img
            src="/assets/aix-logo.png"
            alt="AI Advantage Exchange logo"
            className="h-8 w-auto"
            data-testid="footer-logo-image"
          />
        </div>
        <div className="font-mono-lbl text-xs text-slate-500">
          © 2026 AI Advantage Exchange • Curated online community.
        </div>
        <div className="font-mono-lbl text-xs text-aix-cyandeep">
          <a href="#" className="hover:underline" data-testid="back-to-top-link">
            BACK TO TOP ↑
          </a>
        </div>
      </div>
    </footer>
  );
}
